import React, { useState } from 'react';
import {
  Alert, Button, Form, Modal, Spinner, Table,
} from 'react-bootstrap';
import { COUNTRY_CODES, normalizeCountryCode } from '../../../Utils/countryCodes';

// Offer to replace a booking's billing address with the one Stripe captured
// for its card payment.
//
// Always a per-booking confirmation, never bulk and never automatic. The
// Stripe form is filled in by the CARDHOLDER, who often is not who should be
// invoiced — PU_DASH_6549913 has "FENG HAO, Singapore" on the card against
// "Spa Thermal Travel Kft., Budapest" on the booking. Copying that silently
// would put an individual's home address on a company's tax document, so both
// sides are shown and the current values are always visible.

const FIELDS = [
  ['name', 'Name'],
  ['address', 'Address'],
  ['postcode', 'Postcode'],
  ['city', 'City'],
  ['country', 'Country'],
];

const val = (v) => (v == null || String(v).trim() === '' ? '' : String(v).trim());

export function differs(current = {}, incoming = {}) {
  return FIELDS.some(([k]) => val(current[k]) !== val(incoming[k]));
}

function StripeBillingOverwriteModal({
  show, onHide, current = {}, incoming = {}, onConfirm, saving = false, error = '',
}) {
  const resolved = normalizeCountryCode(incoming.country);
  // When Stripe's free-text country can't be resolved the user picks one —
  // Billingo needs an ISO code and guessing would put a wrong country on an
  // invoice.
  const [countryCode, setCountryCode] = useState(resolved);
  const effective = resolved || countryCode;

  const handleConfirm = () => onConfirm({
    name: val(incoming.name),
    address: val(incoming.address),
    postcode: val(incoming.postcode),
    city: val(incoming.city),
    country: val(incoming.country),
    country_code: effective,
  });

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Overwrite billing details from the Stripe payment?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <p className="text-muted small">
          These are the details the cardholder entered when paying. They are not
          always the party who should be invoiced — check before replacing.
        </p>
        <Table size="sm" bordered className="align-middle">
          <thead>
            <tr>
              <th style={{ width: '18%' }}>Field</th>
              <th style={{ width: '41%' }}>Current (will be replaced)</th>
              <th style={{ width: '41%' }}>From Stripe</th>
            </tr>
          </thead>
          <tbody>
            {FIELDS.map(([key, label]) => {
              const a = val(current[key]);
              const b = val(incoming[key]);
              const changed = a !== b;
              return (
                <tr key={key} className={changed ? 'table-warning' : undefined}>
                  <td className="text-muted small">{label}</td>
                  <td className={changed ? 'text-decoration-line-through text-muted' : ''}>
                    {a || <span className="text-muted fst-italic">empty</span>}
                  </td>
                  <td className={changed ? 'fw-bold' : ''}>
                    {b || <span className="text-muted fst-italic">empty</span>}
                  </td>
                </tr>
              );
            })}
            <tr className={resolved ? undefined : 'table-danger'}>
              <td className="text-muted small">Country code</td>
              <td className="text-muted small">for Billingo</td>
              <td>
                {resolved ? (
                  <span className="font-monospace fw-bold">{resolved}</span>
                ) : (
                  <>
                    <div className="text-danger small mb-1">
                      <i className="fa fa-triangle-exclamation me-1" />
                      {`"${val(incoming.country) || 'empty'}" is not a recognised country — pick one.`}
                    </div>
                    <Form.Select
                      size="sm"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      data-testid="country-picker"
                    >
                      <option value="">Select a country…</option>
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>{`${c.name} (${c.code})`}</option>
                      ))}
                    </Form.Select>
                  </>
                )}
              </td>
            </tr>
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button
          variant="warning"
          onClick={handleConfirm}
          disabled={saving || !effective}
          title={!effective ? 'Pick a country first' : undefined}
        >
          {saving ? <Spinner animation="border" size="sm" className="me-1" /> : (
            <i className="fa fa-arrow-right-arrow-left me-1" />
          )}
          Overwrite billing details
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default StripeBillingOverwriteModal;
